import React, { useContext, useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import i18n from "i18n"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import Typography from "@mui/material/Typography"
import Box from "@mui/material/Box"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import {
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material"
import { NumericFormat } from "react-number-format"
import IconButton from "@mui/material/IconButton"
import CloseIcon from "@mui/icons-material/Close"
import SwapHoriz from "@mui/icons-material/SwapHoriz"
import Budget2023Context from "context/budget2023/Budget2023Context"
import AuthContext from "context/auth/authContext"
import { useBudgetClientPriceTotal } from "hooks/budgeting/useBudgetClientPriceTotal"
import { useDecimalFormatter } from "hooks/useDecimalFormatter"
import { TOTALS, MARGINS } from "constants/decimals"
import { currencyFormatDE } from "lib/transformers"
import { getCurrency } from "utils/functions"

/**
 * % de baja coherente con precio objetivo y precio con baja (decimales dinámicos).
 * @returns {[number, number]} [porcentaje, decimales del %]
 */
function getPercentage(sinBaja, conBaja, nDecimales) {
  if (
    !sinBaja ||
    sinBaja <= 0 ||
    conBaja === undefined ||
    conBaja === null ||
    Number.isNaN(Number(conBaja))
  ) {
    return [0, 2]
  }
  const nDecOut = Math.max(Math.ceil(nDecimales + Math.log10(sinBaja)) - 2, 2)
  const factor = Math.pow(10, nDecOut)
  const percentage = Math.round((1 - conBaja / sinBaja) * 100 * factor) / factor
  return [percentage, nDecOut]
}

/**
 * Precio con baja coherente con objetivo y % de baja.
 * @returns {[number, number]} [precioConBaja, decimales del %]
 */
function getValue(sinBaja, baja, nDecimales) {
  if (
    !sinBaja ||
    sinBaja <= 0 ||
    baja === undefined ||
    baja === null ||
    Number.isNaN(Number(baja))
  ) {
    return [0, 2]
  }
  const nDecOut = Math.max(Math.ceil(nDecimales + Math.log10(sinBaja)) - 2, 2)
  const factorOut = Math.pow(10, nDecOut)
  const factorIn = Math.pow(10, nDecimales)
  const bajaRedondeada = Math.round(baja * factorOut) / factorOut
  const conBaja =
    Math.round(((100 - bajaRedondeada) / 100) * sinBaja * factorIn) / factorIn
  return [conBaja, nDecOut]
}

const readOnlyFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme =>
      theme.palette.mode === "dark"
        ? theme.palette.grey[800]
        : theme.palette.grey[100],
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: theme =>
      theme.palette.mode === "dark"
        ? theme.palette.grey[600]
        : theme.palette.grey[300],
  },
  "& .MuiInputBase-input": {
    cursor: "default",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
}

const readOnlyOfferedClientPriceSx = [
  readOnlyFieldSx,
  {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#ecfef6",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(18, 90, 71, 0.35)",
    },
    "& .MuiInputBase-input": {
      ...readOnlyFieldSx["& .MuiInputBase-input"],
      color: "#125a47",
    },
  },
]

const selectAllText = e => {
  e.target.select()
}

const CloseTenderModal = ({ open, onClose, onConfirmCloseBudget }) => {
  const [submitting, setSubmitting] = useState(false)
  const { budgetTree, marginModal } = useContext(Budget2023Context)
  const { user } = useContext(AuthContext)
  const { getDecimals, getUnitWithMargin, roundToDecimals } =
    useDecimalFormatter()
  const { clientPriceTotalAmount: refClientAmount, formattedClientPriceTotal } =
    useBudgetClientPriceTotal()
  const [draftReduced, setDraftReduced] = useState(null)
  const [draftBajaPercent, setDraftBajaPercent] = useState(null)

  const objectiveNum = useMemo(
    () => (budgetTree ? parseFloat(budgetTree.price) || 0 : 0),
    [budgetTree?.price],
  )

  useEffect(() => {
    if (open && budgetTree) {
      const obj = parseFloat(budgetTree.price) || 0
      const r = parseFloat(budgetTree.priceReduced) || 0
      const dec = getDecimals(TOTALS)
      if (obj > 0) {
        const [pct] = getPercentage(obj, r, dec)
        const [alignedReduced] = getValue(obj, pct, dec)
        setDraftBajaPercent(pct)
        setDraftReduced(alignedReduced)
      } else {
        setDraftBajaPercent(0)
        setDraftReduced(roundToDecimals(r, TOTALS))
      }
    }
  }, [open, budgetTree?.priceReduced, budgetTree?.price])

  const footerTotals = useMemo(() => {
    if (!user || !budgetTree) {
      return {
        reduced: "",
        baja: "",
      }
    }
    const currency = getCurrency(user["custom:currency"])
    const decimals = getDecimals(TOTALS)

    const obj = parseFloat(budgetTree.price) || 0
    const reducedNum = parseFloat(budgetTree.priceReduced) || 0

    let baja = ""
    let reduced = ""
    if (obj > 0) {
      const [bajaPercent, bajaFracDecimals] = getPercentage(
        obj,
        reducedNum,
        decimals,
      )
      const [alignedReduced] = getValue(obj, bajaPercent, decimals)
      baja = `${new Intl.NumberFormat("es-ES", {
        minimumFractionDigits: 0,
        maximumFractionDigits: bajaFracDecimals,
      }).format(bajaPercent)} %`
      reduced = currencyFormatDE(alignedReduced, currency, decimals)
    } else {
      baja = `${new Intl.NumberFormat("es-ES", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(0)} %`
      reduced = currencyFormatDE(
        budgetTree.priceReduced ?? 0,
        currency,
        decimals,
      )
    }

    return { reduced, baja }
  }, [user, getDecimals, budgetTree?.price, budgetTree?.priceReduced])

  const formattedObjectiveTotal = useMemo(() => {
    if (!user || !budgetTree) return ""
    const currency = getCurrency(user["custom:currency"])
    const decimals = getDecimals(TOTALS)
    return currencyFormatDE(
      parseFloat(budgetTree.price) || 0,
      currency,
      decimals,
    )
  }, [user, budgetTree?.price, getDecimals])

  const marginDecimals = getDecimals(MARGINS)
  const totalDecimals = getDecimals(TOTALS)

  const draftBajaOutDecimals = useMemo(() => {
    if (objectiveNum <= 0) {
      return Math.max(marginDecimals, 2)
    }
    return Math.max(Math.ceil(totalDecimals + Math.log10(objectiveNum)) - 2, 2)
  }, [objectiveNum, totalDecimals, marginDecimals])

  const referenceNums = useMemo(() => {
    if (!budgetTree) {
      return { bajaPercent: 0, reduced: 0, bajaDecimals: marginDecimals }
    }
    const obj = parseFloat(budgetTree.price) || 0
    const reduced = parseFloat(budgetTree.priceReduced) || 0
    if (obj <= 0) {
      return { bajaPercent: 0, reduced, bajaDecimals: marginDecimals }
    }
    const [bajaPercent, bajaDecimals] = getPercentage(
      obj,
      reduced,
      totalDecimals,
    )
    const [alignedReduced] = getValue(obj, bajaPercent, totalDecimals)
    return { bajaPercent, reduced: alignedReduced, bajaDecimals }
  }, [
    budgetTree?.price,
    budgetTree?.priceReduced,
    totalDecimals,
    marginDecimals,
  ])

  const margenTotal =
    marginModal.generalExpenses +
    marginModal.indirectExpenses +
    marginModal.industrialProfit

  /** Línea «Ofertada»: valores editables por el usuario que se envían al cerrar la licitación. */
  const tenderOfferedCloseQuery = useMemo(() => {
    if (!budgetTree || objectiveNum <= 0 || draftReduced === null) return null
    const priceReduced = roundToDecimals(draftReduced, TOTALS)
    const reduction = draftBajaPercent ?? 0
    const clientPrice = roundToDecimals(
      getUnitWithMargin(draftReduced, margenTotal),
      getDecimals(TOTALS),
    )
    if (
      !Number.isFinite(priceReduced) ||
      !Number.isFinite(reduction) ||
      !Number.isFinite(clientPrice)
    ) {
      return null
    }
    return { priceReduced, reduction, clientPrice }
  }, [
    budgetTree,
    objectiveNum,
    draftReduced,
    draftBajaPercent,
    margenTotal,
    getDecimals,
    getUnitWithMargin,
    roundToDecimals,
  ])

  const draftClientAmount = useMemo(() => {
    if (draftReduced === null) return null
    return roundToDecimals(
      getUnitWithMargin(draftReduced, margenTotal),
      getDecimals(TOTALS),
    )
  }, [
    draftReduced,
    margenTotal,
    getDecimals,
    getUnitWithMargin,
    roundToDecimals,
  ])

  const formattedClientDraft = useMemo(() => {
    if (!user || draftClientAmount === null) return ""
    return currencyFormatDE(
      draftClientAmount,
      getCurrency(user["custom:currency"]),
      getDecimals(TOTALS),
    )
  }, [user, draftClientAmount, getDecimals])

  const deltaDisplay = useMemo(() => {
    if (
      !user ||
      !budgetTree ||
      draftReduced === null ||
      draftClientAmount === null
    ) {
      return { baja: "", reduced: "", client: "" }
    }
    const currency = getCurrency(user["custom:currency"])
    const decimals = getDecimals(TOTALS)
    const marginDec = getDecimals(MARGINS)

    const dBajaPp = draftBajaPercent - referenceNums.bajaPercent
    const bajaDeltaMaxFrac = Math.max(
      marginDec,
      referenceNums.bajaDecimals ?? 2,
      draftBajaOutDecimals,
    )
    const bajaStr = `${dBajaPp > 0 ? "+" : ""}${new Intl.NumberFormat("es-ES", {
      minimumFractionDigits: 0,
      maximumFractionDigits: bajaDeltaMaxFrac,
    }).format(dBajaPp)} pp`

    const dReduced = draftReduced - referenceNums.reduced
    const reducedStr = currencyFormatDE(dReduced, currency, decimals)

    const dClient = draftClientAmount - refClientAmount
    const clientStr = currencyFormatDE(dClient, currency, decimals)

    return { baja: bajaStr, reduced: reducedStr, client: clientStr }
  }, [
    user,
    budgetTree,
    draftReduced,
    draftClientAmount,
    draftBajaPercent,
    referenceNums.bajaPercent,
    referenceNums.reduced,
    refClientAmount,
    getDecimals,
    draftBajaOutDecimals,
    referenceNums.bajaDecimals,
  ])

  const handleOfferedBajaValueChange = ({ floatValue }, sourceInfo) => {
    if (sourceInfo?.source !== "event") {
      return
    }
    if (
      floatValue === undefined ||
      floatValue === null ||
      Number.isNaN(floatValue)
    ) {
      setDraftBajaPercent(0)
      if (objectiveNum > 0) {
        const [conBaja] = getValue(objectiveNum, 0, totalDecimals)
        setDraftReduced(conBaja)
      } else {
        setDraftReduced(roundToDecimals(0, TOTALS))
      }
      return
    }
    const clamped = Math.min(100, Math.max(0, floatValue))
    setDraftBajaPercent(clamped)
    if (objectiveNum > 0) {
      const [conBaja] = getValue(objectiveNum, clamped, totalDecimals)
      setDraftReduced(conBaja)
    }
  }

  const handleOfferedReducedValueChange = ({ floatValue }, sourceInfo) => {
    if (sourceInfo?.source !== "event") {
      return
    }
    if (
      floatValue === undefined ||
      floatValue === null ||
      Number.isNaN(floatValue)
    ) {
      setDraftReduced(roundToDecimals(0, TOTALS))
      setDraftBajaPercent(objectiveNum > 0 ? 100 : 0)
      return
    }
    let v = floatValue
    if (objectiveNum > 0) {
      v = Math.min(objectiveNum, Math.max(0, v))
    } else {
      v = Math.max(0, v)
    }
    const roundedReduced = roundToDecimals(v, TOTALS)
    setDraftReduced(roundedReduced)
    if (objectiveNum > 0) {
      const [pct] = getPercentage(objectiveNum, roundedReduced, totalDecimals)
      setDraftBajaPercent(pct)
    } else {
      setDraftBajaPercent(0)
    }
  }

  const handleConfirm = async () => {
    setSubmitting(true)
    try {
      await onConfirmCloseBudget(tenderOfferedCloseQuery)
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  const emptyEmDash = "—"

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!submitting) onClose()
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {i18n.t("closeTenderModal.title")}
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={submitting}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pb: 0.5 }}>
          <Typography variant="body2">
            {i18n.t("closeTenderModal.description")}
          </Typography>

          <TableContainer component={Paper} variant="outlined" sx={{ mt: 0.5 }}>
            <Table size="small" sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "14%", fontWeight: 600 }}>
                    {i18n.t("closeTenderModal.colType")}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ width: "20%", fontWeight: 600 }}
                  >
                    {i18n.t("closeTenderModal.colCosteObjetivoTotal")}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {i18n.t("closeTenderModal.colBajaGlobal")}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {i18n.t("closeTenderModal.colCosteConBajas")}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {i18n.t("closeTenderModal.colPrecioConMargenes")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow hover={false}>
                  <TableCell component="th" scope="row">
                    {i18n.t("closeTenderModal.rowProjected")}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1 }}>
                    <TextField
                      hiddenLabel
                      fullWidth
                      size="small"
                      value={
                        !user || !budgetTree
                          ? emptyEmDash
                          : formattedObjectiveTotal
                      }
                      inputProps={{
                        "aria-label": i18n.t(
                          "closeTenderModal.colCosteObjetivoTotal",
                        ),
                      }}
                      InputProps={{ readOnly: true }}
                      sx={readOnlyFieldSx}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1 }}>
                    <TextField
                      hiddenLabel
                      fullWidth
                      size="small"
                      value={
                        !user || !budgetTree ? emptyEmDash : footerTotals.baja
                      }
                      InputProps={{ readOnly: true }}
                      sx={readOnlyFieldSx}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1 }}>
                    <TextField
                      hiddenLabel
                      fullWidth
                      size="small"
                      value={
                        !user || !budgetTree
                          ? emptyEmDash
                          : footerTotals.reduced
                      }
                      InputProps={{ readOnly: true }}
                      sx={readOnlyFieldSx}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1 }}>
                    <TextField
                      hiddenLabel
                      fullWidth
                      size="small"
                      value={
                        !user || !budgetTree
                          ? emptyEmDash
                          : formattedClientPriceTotal
                      }
                      InputProps={{ readOnly: true }}
                      sx={readOnlyFieldSx}
                    />
                  </TableCell>
                </TableRow>

                <TableRow hover={false}>
                  <TableCell component="th" scope="row">
                    {i18n.t("closeTenderModal.rowOffered")}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1 }}>
                    <TextField
                      hiddenLabel
                      fullWidth
                      size="small"
                      value={
                        !user || !budgetTree
                          ? emptyEmDash
                          : formattedObjectiveTotal
                      }
                      inputProps={{
                        "aria-label": i18n.t(
                          "closeTenderModal.colCosteObjetivoTotal",
                        ),
                      }}
                      InputProps={{ readOnly: true }}
                      sx={readOnlyFieldSx}
                    />
                  </TableCell>
                  <TableCell
                    colSpan={2}
                    align="right"
                    sx={{ py: 1, verticalAlign: "middle" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 1,
                        flexWrap: { xs: "wrap", sm: "nowrap" },
                      }}
                    >
                      <NumericFormat
                        customInput={TextField}
                        hiddenLabel
                        fullWidth
                        size="small"
                        sx={{ flex: 1, minWidth: 0, maxWidth: { sm: 220 } }}
                        value={
                          draftBajaPercent === null ? "" : draftBajaPercent
                        }
                        thousandSeparator={false}
                        decimalSeparator=","
                        decimalScale={draftBajaOutDecimals}
                        fixedDecimalScale={false}
                        allowNegative={false}
                        disabled={!user || !budgetTree || objectiveNum <= 0}
                        onClick={selectAllText}
                        onFocus={selectAllText}
                        isAllowed={values => {
                          const { floatValue } = values
                          if (floatValue === undefined || floatValue === null) {
                            return true
                          }
                          if (floatValue < 0 || floatValue > 100) return false
                          return true
                        }}
                        onValueChange={handleOfferedBajaValueChange}
                        inputProps={{
                          "aria-label": i18n.t(
                            "closeTenderModal.ariaBajaOfertada",
                          ),
                          style: { textAlign: "right" },
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                          ),
                        }}
                      />
                      <SwapHoriz
                        sx={{
                          color: "text.secondary",
                          flexShrink: 0,
                          alignSelf: "center",
                        }}
                        fontSize="small"
                        aria-hidden
                      />
                      <NumericFormat
                        customInput={TextField}
                        hiddenLabel
                        fullWidth
                        size="small"
                        sx={{ flex: 1, minWidth: 0, maxWidth: { sm: 260 } }}
                        value={draftReduced === null ? "" : draftReduced}
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={totalDecimals}
                        fixedDecimalScale
                        allowNegative={false}
                        disabled={!user || !budgetTree}
                        onClick={selectAllText}
                        onFocus={selectAllText}
                        isAllowed={values => {
                          const { floatValue } = values
                          if (floatValue === undefined || floatValue === null) {
                            return true
                          }
                          if (floatValue < 0) return false
                          if (objectiveNum > 0 && floatValue > objectiveNum) {
                            return false
                          }
                          return true
                        }}
                        onValueChange={handleOfferedReducedValueChange}
                        inputProps={{
                          "aria-label": i18n.t(
                            "closeTenderModal.ariaCosteOfertado",
                          ),
                          style: { textAlign: "right" },
                        }}
                        InputProps={{
                          endAdornment: user ? (
                            <InputAdornment position="end" sx={{ ml: 0 }}>
                              {getCurrency(user["custom:currency"])}
                            </InputAdornment>
                          ) : undefined,
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1 }}>
                    <TextField
                      hiddenLabel
                      fullWidth
                      size="small"
                      value={formattedClientDraft || emptyEmDash}
                      InputProps={{ readOnly: true }}
                      sx={readOnlyOfferedClientPriceSx}
                    />
                  </TableCell>
                </TableRow>

                <TableRow hover={false}>
                  <TableCell component="th" scope="row">
                    {i18n.t("closeTenderModal.rowDelta")}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1 }}>
                    <TextField
                      hiddenLabel
                      fullWidth
                      size="small"
                      value={emptyEmDash}
                      inputProps={{
                        "aria-label": i18n.t(
                          "closeTenderModal.colCosteObjetivoTotal",
                        ),
                      }}
                      InputProps={{ readOnly: true }}
                      sx={readOnlyFieldSx}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1 }}>
                    <TextField
                      hiddenLabel
                      fullWidth
                      size="small"
                      value={deltaDisplay.baja || emptyEmDash}
                      InputProps={{ readOnly: true }}
                      sx={readOnlyFieldSx}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1 }}>
                    <TextField
                      hiddenLabel
                      fullWidth
                      size="small"
                      value={deltaDisplay.reduced || emptyEmDash}
                      InputProps={{ readOnly: true }}
                      sx={readOnlyFieldSx}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ py: 1 }}>
                    <TextField
                      hiddenLabel
                      fullWidth
                      size="small"
                      value={deltaDisplay.client || emptyEmDash}
                      InputProps={{ readOnly: true }}
                      sx={readOnlyFieldSx}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          color="secondary"
          variant="text"
          disabled={submitting}
        >
          {i18n.t("buttons.cancel")}
        </Button>
        <Button
          onClick={() => void handleConfirm()}
          color="branded"
          variant="contained"
          disabled={
            submitting ||
            draftReduced === null ||
            (objectiveNum > 0 && !tenderOfferedCloseQuery)
          }
          sx={{ minWidth: 175, height: 36 }}
        >
          {submitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            i18n.t("Cerrar presupuesto")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

CloseTenderModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  /** Recibe `null` o `{ priceReduced, reduction, clientPrice }` (línea ofertada, modo licitación). */
  onConfirmCloseBudget: PropTypes.func.isRequired,
}

export default CloseTenderModal
